'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';

import Modal from '@/components/ui/Modal';
import { Eye, EyeOff, CheckCircle, Shield, ArrowRight, Lock, User, ArrowLeft, Mail, HelpCircle } from 'lucide-react';
import { Toaster, toast } from 'sonner';

interface LoginForm {
  username: string;
  password: string;
  role: 'admin' | 'staff';
  organizationCode?: string;
}

interface PasswordChangeForm {
  newPassword: string;
  confirmPassword: string;
}

const OWNER_CREDENTIALS_KEY = 'owner_credentials_configured';
const CO_OWNER_CREDENTIALS_KEY = 'coowner_credentials_configured';

// Seed default co-owner credentials if not yet set (so login works before owner dashboard is visited)
if (typeof window !== 'undefined' && !localStorage.getItem(CO_OWNER_CREDENTIALS_KEY)) {
  localStorage.setItem(CO_OWNER_CREDENTIALS_KEY, JSON.stringify({
    username: 'coowner',
    password: 'CoOwner@2025',
    createdAt: new Date().toISOString(),
  }));
}

export default function LoginClient() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [pendingRoute, setPendingRoute] = useState('');
  const [pendingOrgCode, setPendingOrgCode] = useState('');
  const [pendingStaffUsername, setPendingStaffUsername] = useState('');
  const [rememberMe, setRememberMe] = useState(false);

  const { register, handleSubmit, setError, watch, formState: { errors } } = useForm<LoginForm>({
    defaultValues: { role: 'admin' }
  });

  const selectedRole = watch('role');

  const {
    register: registerPw,
    handleSubmit: handlePwSubmit,
    watch: watchPw,
    formState: { errors: pwErrors }
  } = useForm<PasswordChangeForm>();

  // Backend integration point: POST /api/auth/login
  const onSubmit = async (data: LoginForm) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 1000));

    // Silently check owner credentials first — no role tab exposed
    const ownerConfigured = typeof window !== 'undefined'
      ? localStorage.getItem(OWNER_CREDENTIALS_KEY)
      : null;

    if (ownerConfigured) {
      try {
        const stored = JSON.parse(ownerConfigured);
        if (data.username === stored.username && data.password === stored.password) {
          setIsLoading(false);
          toast.success('Welcome back!');
          setTimeout(() => router.push('/owner-dashboard'), 600);
          return;
        }
      } catch {
        // parse error — continue to normal login
      }
    }

    // Silently check co-owner credentials
    const coOwnerConfigured = typeof window !== 'undefined'
      ? localStorage.getItem(CO_OWNER_CREDENTIALS_KEY)
      : null;

    if (coOwnerConfigured) {
      try {
        const coStored = JSON.parse(coOwnerConfigured);
        if (data.username === coStored.username && data.password === coStored.password) {
          setIsLoading(false);
          toast.success('Welcome back!');
          setTimeout(() => router.push('/owner-dashboard'), 600);
          return;
        }
      } catch {
        // parse error — continue to normal login
      }
    }

    // Admin / Staff login — org code is optional; validate only if provided
    const enteredCode = data.organizationCode?.trim() || '';
    const registeredCodes: string[] = JSON.parse(
      (typeof window !== 'undefined' ? localStorage.getItem('registered_org_codes') : null) || '[]'
    );
    if (enteredCode && !registeredCodes.includes(enteredCode)) {
      setIsLoading(false);
      setError('organizationCode', { message: 'Invalid organization code — contact your owner for the correct code' });
      return;
    }

    // Validate admin credentials against stored credentials for this org code
    if (data.role === 'admin') {
      const adminCredentials: Record<string, { username: string; password: string; orgCode: string; orgName: string; firstLogin: boolean }> =
        JSON.parse((typeof window !== 'undefined' ? localStorage.getItem('registered_admin_credentials') : null) || '{}');
      const orgAdmin = adminCredentials[enteredCode];

      if (!orgAdmin) {
        setIsLoading(false);
        setError('password', { message: enteredCode ? 'No admin account found for this organization code' : 'Invalid username or password' });
        return;
      }

      if (data.username !== orgAdmin.username) {
        setIsLoading(false);
        setError('username', { message: 'Invalid username for this organization' });
        return;
      }

      if (data.password !== orgAdmin.password) {
        setIsLoading(false);
        setError('password', { message: 'Incorrect password — use the temporary password issued during onboarding' });
        return;
      }

      // Credentials match — check if first login
      if (orgAdmin.firstLogin) {
        // Mark first login as used and prompt password change
        orgAdmin.firstLogin = false;
        adminCredentials[enteredCode] = orgAdmin;
        if (typeof window !== 'undefined') {
          localStorage.setItem('registered_admin_credentials', JSON.stringify(adminCredentials));
          localStorage.setItem('current_admin_org', JSON.stringify({ orgCode: enteredCode, orgName: orgAdmin.orgName, username: orgAdmin.username }));
        }
        setIsLoading(false);
        setPendingRoute('/admin-dashboard');
        setPendingOrgCode(enteredCode);
        setShowPasswordChange(true);
        return;
      }

      // Normal login
      if (typeof window !== 'undefined') {
        localStorage.setItem('current_admin_org', JSON.stringify({ orgCode: enteredCode, orgName: orgAdmin.orgName, username: orgAdmin.username }));
      }
      setIsLoading(false);
      toast.success(`Welcome back, ${orgAdmin.username}!`);
      setTimeout(() => router.push('/admin-dashboard'), 600);
      return;
    }

    // Staff login — validate credentials
    if (data.role === 'staff') {
      const staffCredentials: Record<string, { username: string; password: string; name: string; orgCode: string; orgName: string; firstLogin: boolean }> =
        JSON.parse((typeof window !== 'undefined' ? localStorage.getItem('registered_staff_credentials') : null) || '{}');
      const normalizedUsername = data.username.trim().toLowerCase();
      const staffMember = staffCredentials[normalizedUsername];

      if (!staffMember) {
        setIsLoading(false);
        setError('username', { message: 'No staff account found with this username' });
        return;
      }

      if (staffMember.orgCode !== enteredCode) {
        setIsLoading(false);
        setError('organizationCode', { message: 'Username does not belong to this organization' });
        return;
      }

      if (data.password !== staffMember.password) {
        setIsLoading(false);
        setError('password', { message: 'Incorrect password' });
        return;
      }

      // Save staff session
      if (typeof window !== 'undefined') {
        localStorage.setItem('current_staff_session', JSON.stringify({
          name: staffMember.name,
          orgCode: staffMember.orgCode,
          orgName: staffMember.orgName,
          username: staffMember.username,
        }));
        // Also set generic session for getSession() compatibility
        localStorage.setItem('current_session', JSON.stringify({
          role: 'staff',
          name: staffMember.name,
          orgCode: staffMember.orgCode,
          orgName: staffMember.orgName,
        }));
      }

      // Check if first login — prompt password change
      if (staffMember.firstLogin) {
        staffMember.firstLogin = false;
        staffCredentials[normalizedUsername] = staffMember;
        if (typeof window !== 'undefined') {
          localStorage.setItem('registered_staff_credentials', JSON.stringify(staffCredentials));
        }
        setIsLoading(false);
        setPendingRoute('/staff-dashboard');
        setPendingOrgCode(enteredCode);
        setPendingStaffUsername(data.username);
        setShowPasswordChange(true);
        return;
      }

      setIsLoading(false);
      toast.success(`Welcome, ${staffMember.name}!`);
      setTimeout(() => router.push('/staff-dashboard'), 600);
      return;
    }

    setIsLoading(false);
    toast.success('Welcome!');
    setTimeout(() => router.push('/staff-dashboard'), 600);
  };

  // Backend integration point: POST /api/auth/change-password
  const onPasswordChange = async (data: PasswordChangeForm) => {
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 800));

    // Persist the new password so subsequent logins use it
    if (typeof window !== 'undefined') {
      if (pendingRoute === '/admin-dashboard' && pendingOrgCode) {
        const adminCredentials: Record<string, { username: string; password: string; orgCode: string; orgName: string; firstLogin: boolean }> =
          JSON.parse(localStorage.getItem('registered_admin_credentials') || '{}');
        if (adminCredentials[pendingOrgCode]) {
          adminCredentials[pendingOrgCode].password = data.newPassword;
          localStorage.setItem('registered_admin_credentials', JSON.stringify(adminCredentials));
        }
      } else if (pendingRoute === '/staff-dashboard' && pendingStaffUsername) {
        const staffCredentials: Record<string, { username: string; password: string; name: string; orgCode: string; orgName: string; firstLogin: boolean }> =
          JSON.parse(localStorage.getItem('registered_staff_credentials') || '{}');
        if (staffCredentials[pendingStaffUsername]) {
          staffCredentials[pendingStaffUsername].password = data.newPassword;
          localStorage.setItem('registered_staff_credentials', JSON.stringify(staffCredentials));
        }
      }
    }

    toast.success('Password updated successfully. Welcome!');
    setShowPasswordChange(false);
    setTimeout(() => router.push(pendingRoute), 600);
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen flex bg-background">
      <Toaster position="bottom-right" richColors />

      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[480px] xl:w-[520px] p-10 flex-shrink-0 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 50%, #1B5E20 100%)' }}>
        {/* Decorative pattern overlay */}
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        {/* Decorative gradient orbs */}
        <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #4CAF50 0%, transparent 70%)' }}></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #4CAF50 0%, transparent 70%)' }}></div>

        <div className="relative z-10 flex items-center gap-3">
          <AppLogo
            src="/assets/images/MERISTEM-LOGO_ob-1781817309617.png"
            size={40}
          />
          <div>
            <p className="text-white font-700 text-lg leading-tight">MERISTEM DIGITAL</p>
            <p className="text-white/60 text-xs">Digital Solutions</p>
          </div>
        </div>

        <div className="relative z-10">
          <div className="mb-8">
            <h1 className="text-white text-3xl font-800 leading-snug mb-4">
              Welcome to<br />Meristem Digital
            </h1>
            <p className="text-white/70 text-sm leading-relaxed">
              Your trusted partner for batch payments, ceremony management, and digital solutions across Africa.
            </p>
          </div>

          <div className="space-y-3">
            {[
              'Batch payment processing',
              'Ceremony & contribution management',
              'Secure digital wallet solutions',
            ].map((feat) => (
              <div key={`feat-${feat}`} className="flex items-center gap-3">
                <CheckCircle size={16} className="text-accent flex-shrink-0" />
                <span className="text-white/80 text-sm">{feat}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-white/40 text-xs">
          © 2026 Meristem Digital. All rights reserved.
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex flex-col">
        {/* Back to Home link */}
        <div className="p-4 lg:p-6">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Home</span>
          </Link>
        </div>

        {/* Form container */}
        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md animate-fade-in">
            {/* Mobile logo */}
            <div className="flex items-center gap-2 mb-8 lg:hidden">
              <AppLogo
                src="/assets/images/MERISTEM-LOGO_ob-1781817309617.png"
                size={32}
              />
              <span className="font-700 text-primary">MERISTEM DIGITAL</span>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-700 text-foreground mb-2">Welcome back!</h2>
              <p className="text-sm text-muted-foreground">Sign in to access your dashboard</p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {/* Role selector — Admin and Staff only */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-2">Account Type</label>
                <div className="grid grid-cols-2 gap-3">
                  {(['admin', 'staff'] as const).map((r) => (
                    <label key={`role-${r}`} className="cursor-pointer">
                      <input type="radio" value={r} {...register('role')} className="sr-only" />
                      <div className={`text-center py-2.5 px-3 rounded-lg border-2 text-sm font-600 transition-all cursor-pointer ${
                        selectedRole === r
                          ? 'border-primary bg-primary text-white shadow-sm'
                          : 'border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground'
                      }`}>
                        {r.charAt(0).toUpperCase() + r.slice(1)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Organization Code — optional */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-2">Organization Code <span className="text-muted-foreground font-400">(optional)</span></label>
                <div className="relative">
                  <Shield size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Enter your organization code"
                    className="input-field w-full pl-9 pr-4 py-3 text-sm"
                    suppressHydrationWarning
                    {...register('organizationCode')}
                  />
                </div>
                {errors.organizationCode && <p className="text-xs text-danger mt-1.5">{errors.organizationCode.message}</p>}
              </div>

              {/* Username */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-2">Username</label>
                <div className="relative">
                  <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Enter your username"
                    className="input-field w-full pl-9 pr-4 py-3 text-sm"
                    suppressHydrationWarning
                    {...register('username', { required: 'Username is required' })}
                  />
                </div>
                {errors.username && <p className="text-xs text-danger mt-1.5">{errors.username.message}</p>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-sm font-600 text-foreground mb-2">Password</label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className="input-field w-full pl-9 pr-10 py-3 text-sm"
                    suppressHydrationWarning
                    {...register('password', { required: 'Password is required' })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    suppressHydrationWarning
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {errors.password && <p className="text-xs text-danger mt-1.5">{errors.password.message}</p>}
              </div>

              {/* Remember me & Forgot password */}
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-0 cursor-pointer"
                  />
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">Remember me</span>
                </label>
                <a
                  href="mailto:support@meristem.co.tz?subject=Password%20Reset%20Request"
                  className="text-sm text-primary hover:text-primary-light font-500 transition-colors"
                >
                  Forgot password?
                </a>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                suppressHydrationWarning
                className="btn-primary w-full py-3 rounded-lg text-sm font-600 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                ) : (
                  <>Sign In <ArrowRight size={16} /></>
                )}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-background text-muted-foreground">Need help?</span>
              </div>
            </div>

            {/* Contact support */}
            <div className="text-center space-y-3">
              <a
                href="mailto:support@meristem.co.tz?subject=Support%20Request"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors group"
              >
                <Mail size={16} className="group-hover:scale-110 transition-transform" />
                <span>Contact support</span>
              </a>
              <p className="text-xs text-muted-foreground">
                Having trouble signing in? We're here to help.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* First-login password change modal */}
      <Modal
        open={showPasswordChange}
        onClose={() => {}}
        title="Set Your New Password"
        subtitle="This is your first login. You must set a new password before continuing."
        size="sm"
        hideClose
      >
        <div className="flex items-center gap-2 p-3 rounded-lg bg-warning-bg border border-warning/20 mb-5">
          <Shield size={16} className="text-warning flex-shrink-0" />
          <p className="text-xs text-warning font-500">Your temporary password must be changed before you can access the portal.</p>
        </div>

        <form onSubmit={handlePwSubmit(onPasswordChange)} className="space-y-4">
          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">New Password</label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                placeholder="Minimum 8 characters"
                className="input-field w-full pr-10 px-3 py-2.5 text-sm"
                suppressHydrationWarning
                {...registerPw('newPassword', {
                  required: 'New password is required',
                  minLength: { value: 8, message: 'Password must be at least 8 characters' },
                  pattern: { value: /(?=.*[A-Z])(?=.*[0-9])/, message: 'Must include uppercase and number' }
                })}
              />
              <button type="button" onClick={() => setShowNewPassword(!showNewPassword)}
                suppressHydrationWarning
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {pwErrors.newPassword && <p className="text-xs text-danger mt-1">{pwErrors.newPassword.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-600 text-foreground mb-1.5">Confirm Password</label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Re-enter new password"
                className="input-field w-full pr-10 px-3 py-2.5 text-sm"
                suppressHydrationWarning
                {...registerPw('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: v => v === watchPw('newPassword') || 'Passwords do not match'
                })}
              />
              <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                suppressHydrationWarning
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showConfirmPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {pwErrors.confirmPassword && <p className="text-xs text-danger mt-1">{pwErrors.confirmPassword.message}</p>}
          </div>

          <button
            type="submit"
            disabled={isLoading}
            suppressHydrationWarning
            className="btn-primary w-full py-3 rounded-lg text-sm font-600 flex items-center justify-center gap-2 mt-2"
          >
            {isLoading ? (
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : 'Set Password & Continue'}
          </button>
        </form>
      </Modal>
    </div>
  );
}